<template>
  <div class="tl-page">
    <Header />

    <div class="tl-calendar-app">
      <!-- ── Header Bar ── -->
      <div class="tl-header-bar">
        <div class="tl-header-left">
          <h1 class="tl-page-title">
            <svg class="tl-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 10l-6-6M2 10l6-6M22 10H2M22 10c0 5-9 11-9 11s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            Ocean Calendar
          </h1>
        </div>

        <!-- ── Navigation Controls ── -->
        <div class="tl-header-center">
          <div class="tl-nav-controls">
            <button class="tl-nav-btn" @click="prev" title="Previous">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <h2 class="tl-current-range">{{ currentTitle }}</h2>
            <button class="tl-nav-btn" @click="next" title="Next">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <button class="tl-btn-today" @click="today">Today</button>
        </div>

        <div class="tl-header-actions">
          <div class="tl-btn-group">
            <button class="tl-btn-tab" :class="{ 'is-active': currentView === 'dayGridMonth' }"
              @click="changeView('dayGridMonth')">Month</button>
            <button class="tl-btn-tab" :class="{ 'is-active': currentView === 'dayGridWeek' }"
              @click="changeView('dayGridWeek')">Week</button>
            <button class="tl-btn-tab" :class="{ 'is-active': currentView === 'dayGridYear' }"
              @click="changeView('dayGridYear')">Year</button>
          </div>
          <button class="tl-btn tl-btn--primary" @click="downloadPDF">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            PDF
          </button>
        </div>
      </div>

      <!-- ── Calendar Container ── -->
      <div class="tl-calendar-container" id="calendar-capture">
        <FullCalendar :options="calendarOptions" ref="calendar" />
      </div>
    </div>

    <!-- ── Modern Tooltip ── -->
    <div v-if="tooltipVisible" class="tl-event-tooltip" :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }">
      <div class="tl-tooltip-header">{{ selectedEvent.title }}</div>
      <div class="tl-tooltip-body" v-html="selectedEvent.description"></div>
    </div>
  </div>
</template>

<script>
import Header from './Header.vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export default {
  name: 'Oceancalender',
  components: {
    Header,
    FullCalendar
  },
  data() {
    return {
      currentView: 'dayGridMonth',
      currentTitle: '',
      selectedEvent: null,
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        headerToolbar: false,
        dayMaxEvents: false, // Show all entries
        eventDisplay: 'block',
        height: 'auto',
        contentHeight: 'auto',
        firstDay: 1, // Start on Monday
        eventDidMount: this.customEventRender,
        datesSet: this.handleDatesSet,
        events: [],
        views: {
          dayGridYear: {
            duration: { years: 1 }
          }
        }
      }
    }
  },
  created() {
    this.fetchEvents();
  },
  methods: {
    fetchEvents() {
      this.$xhr.get('oceancalender.json').then(response => {
        const shipSchedules = response.data.slice(1);
        const groupByDate = {};

        shipSchedules.forEach(schedule => {
          if (!schedule.Date) return;
          const date = schedule.Date.substring(0, 10);
          const shipCode = `${schedule.Ship}_${schedule.Code}`;
          if (!groupByDate[date]) groupByDate[date] = {};
          if (!groupByDate[date][shipCode]) groupByDate[date][shipCode] = [];
          groupByDate[date][shipCode].push(schedule);
        });

        const events = [];
        Object.keys(groupByDate).forEach(date => {
          Object.keys(groupByDate[date]).forEach(shipCode => {
            const schedules = groupByDate[date][shipCode];
            const ship = schedules[0].Ship;
            const port = schedules[0].Port;
            const cruiseLine = schedules[0]['Cruise Line'] || '';
            const start = schedules[0].Start || '';
            const end = schedules[0].End || '';
            const lp = schedules[0].LP || '';

            const title = `${ship} in ${port}`;
            const description = `
              <strong>Cruise Line:</strong> ${cruiseLine}<br>
              <strong>Port:</strong> ${port}<br>
              <strong>Start:</strong> ${start}<br>
              <strong>End:</strong> ${end}<br>
              <strong>LP:</strong> ${lp}
            `;

            // Original Port-based Colors (Exact Revert)
            let backgroundColor = '';
            let textColor = '';

            switch (port) {
              case 'Kiel':
                backgroundColor = 'darkorange';
                textColor = 'white';
                break;
              case 'Hamburg':
                backgroundColor = 'purple';
                textColor = 'white';
                break;
              case 'Binz':
              case 'Wismar':
              case 'Sassnitz':
                backgroundColor = 'black';
                textColor = 'white';
                break;
              case 'Rostock':
                backgroundColor = 'darkred';
                break;
              case 'Travemunde':
              case 'Helgoland':
                backgroundColor = 'green';
                textColor = 'white';
                break;
              case 'Warnemunde':
                backgroundColor = '';
                textColor = 'white';
                break;
            }

            events.push({
              title,
              start: date,
              allDay: true,
              backgroundColor,
              borderColor: backgroundColor,
              textColor,
              extendedProps: { description }
            });
          });
        });
        this.calendarOptions.events = events;
      });
    },
    customEventRender(info) {
      // Hover Tooltip Logic
      info.el.addEventListener('mouseenter', (event) => {
        this.selectedEvent = {
          title: info.event.title,
          description: info.event.extendedProps.description
        };
        this.tooltipX = event.clientX + 10;
        this.tooltipY = event.clientY + 10;
        this.tooltipVisible = true;
      });

      info.el.addEventListener('mouseleave', () => {
        this.tooltipVisible = false;
      });
    },
    handleDatesSet(arg) {
      this.currentTitle = arg.view.title;
    },
    getContrastColor(hexcolor) {
      if (!hexcolor) return '#ffffff';
      if (hexcolor.startsWith('#')) hexcolor = hexcolor.slice(1);
      if (hexcolor.length === 3) hexcolor = hexcolor.split('').map(c => c + c).join('');
      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? '#111827' : '#ffffff';
    },
    changeView(view) {
      this.currentView = view;
      this.$refs.calendar.getApi().changeView(view);
    },
    next() {
      this.$refs.calendar.getApi().next();
    },
    prev() {
      this.$refs.calendar.getApi().prev();
    },
    today() {
      this.$refs.calendar.getApi().today();
    },
    async downloadPDF(e) {
      e.preventDefault();
      const element = document.querySelector('.tl-calendar-app');
      if (!element) return;

      const originalStyle = element.style.cssText;
      element.style.width = '2000px';
      element.style.maxWidth = 'none';
      element.style.height = 'auto';
      element.style.overflow = 'visible';

      try {
        window.dispatchEvent(new Event('resize'));
        await new Promise(resolve => setTimeout(resolve, 300));

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 2000,
          windowHeight: element.scrollHeight + 100
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const pdfWidth = canvas.width;
        const pdfHeight = canvas.height;

        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'l' : 'p',
          unit: 'px',
          format: [pdfWidth, pdfHeight]
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        pdf.save(`Ocean_Calendar_${this.currentTitle.replace(/\s+/g, '_')}.pdf`);
      } catch (error) {
        console.error('PDF Generation failed:', error);
      } finally {
        element.style.cssText = originalStyle;
        window.dispatchEvent(new Event('resize'));
      }
    }
  }
}
</script>

<style scoped>
/* ────────────── Page Layout ────────────── */
.tl-page {
  background: #f8fafc;
  min-height: 100vh;
}

.tl-calendar-app {
  padding: 24px 20px;
  max-width: 100%;
  margin: 0 auto;
}

/* ────────────── Header Bar ────────────── */
.tl-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-header-left {
  flex: 1;
}

.tl-page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  text-transform: uppercase;
}

.tl-title-icon {
  width: 24px;
  height: 24px;
  color: #3b82f6;
  /* Ocean Blue */
}

/* Header Center - Navigation */
.tl-header-center {
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tl-nav-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tl-nav-btn {
  background: #f1f5f9;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.tl-nav-btn:hover {
  background: #e2e8f0;
  color: #3b82f6;
}

.tl-current-range {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1e293b;
  min-width: 200px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tl-btn-today {
  font-size: 0.7rem;
  font-weight: 800;
  color: #3b82f6;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  padding: 2px 10px;
  border-radius: 20px;
  cursor: pointer;
  text-transform: uppercase;
}

.tl-header-actions {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

/* ────────────── Tabs & Groups ────────────── */
.tl-btn-group {
  display: flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
}

.tl-btn-tab {
  border: none;
  background: transparent;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s;
}

.tl-btn-tab.is-active {
  background: #fff;
  color: #1e293b;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* ────────────── Buttons ────────────── */
.tl-btn {
  height: 34px;
  padding: 0 14px;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
}

.tl-btn--primary {
  background: #3b82f6;
  color: white;
}

/* ────────────── Calendar ────────────── */
.tl-calendar-container {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

::v-deep .fc {
  --fc-border-color: #f1f5f9;
  --fc-daygrid-event-dot-width: 6px;
  --fc-today-bg-color: #eff6ff;
  font-family: inherit;
}

::v-deep .fc-toolbar {
  display: none !important;
}

::v-deep .fc-col-header-cell {
  background: #f8fafc;
  padding: 16px 0;
  border-bottom: 2px solid #e2e8f0;
}

::v-deep .fc-col-header-cell-cushion {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.1em;
  text-decoration: none !important;
}

::v-deep .fc-daygrid-day-frame {
  min-height: 150px !important;
}

::v-deep .fc-daygrid-day-number {
  font-size: 0.95rem;
  font-weight: 700;
  color: #475569;
  padding: 10px 14px !important;
  text-decoration: none !important;
}

::v-deep .fc-day-today .fc-daygrid-day-number {
  color: #3b82f6;
  font-weight: 900;
  background: #dbeafe;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 4px;
}

/* Events Styling */
::v-deep .fc-event {
  border-radius: 50px !important;
  margin: 2px 4px !important;
  border: none !important;
  cursor: pointer;
}

::v-deep .fc-event-main {
  padding: 4px 10px;
  line-height: 1.4;
  color: inherit !important;
}

::v-deep .fc-event-title {
  font-size: 0.78rem;
  font-weight: 800;
  white-space: normal;
  word-break: break-word;
  color: #ffffff !important;
  /* Force white text */
}

::v-deep .tl-event-subtitle {
  font-size: 0.6rem;
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #ffffff !important;
  /* Force white text */
}

/* ────────────── Tooltip ────────────── */
.tl-event-tooltip {
  position: fixed;
  background: white;
  min-width: 240px;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  padding: 16px;
  z-index: 9999;
  border: 1px solid #e2e8f0;
  pointer-events: none;
  /* Let clicks pass through */
}

.tl-tooltip-header {
  font-weight: 800;
  font-size: 0.9rem;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 8px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.tl-tooltip-body {
  font-size: 0.8rem;
  color: #475569;
  line-height: 1.6;
}
</style>
